import React from 'react';
import axios from 'axios';
import parse from 'html-react-parser';

import { AsyncAutosuggest } from '@auspost/postmaster-react';

import { apiEndpoint } from 'config/track-config';

import { SearchResult } from '../CalculatorModals/AddressLookupModal';
import { INLINE_EDD_INPUT_ERROR_TEST_ID, INLINE_EDD_SEARCH_INPUT_TEST_ID } from '../InlineEdd.test-ids';

import styles from '../InlineEdd.module.scss';

interface AddressLookupResult {
  postcode: number;
  location: string;
  state: string;
}

const apiGetPostcodeLookup = (query: string) => {
  const requestConfig = {
    headers: {
      'Content-Type': 'application/json',
      'AUTH-KEY': apiEndpoint.apiKeys.postcodeLookup,
    },
    params: {
      limit: '10',
      q: query,
    },
  };
  return axios.get(apiEndpoint.postcodeLookup, requestConfig);
};

interface AddressAutosuggestProps {
  handleManualMode?: () => void;
  selectOption: (result: SearchResult | null) => void;
}

const AddressAutosuggest: React.FC<AddressAutosuggestProps> = ({ handleManualMode, selectOption }) => {
  const [value, setValue] = React.useState('');
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const getLocalityData = async (value: string) => {
    const res = await apiGetPostcodeLookup(value);
    setIsLoading(false);
    const locality = res?.data?.localities?.locality;
    if (!Array.isArray(locality)) return [locality];
    return locality;
  };

  const handleOnInput = (e: React.FormEvent<HTMLInputElement>) => {
    selectOption(null);
    setError('');
    e.currentTarget.setCustomValidity('Please enter a valid suburb or postcode');
    if (e.currentTarget.value.length >= 3 && e.currentTarget.value.trim() !== value) setIsLoading(true);
    if (e.currentTarget.validity.valueMissing) e.currentTarget.reportValidity();
  };

  const handleOnInvalid = (e: React.FormEvent<HTMLInputElement>) => {
    e.preventDefault();
    const validity = e.currentTarget.validity;
    if (validity.customError) e.currentTarget.setCustomValidity('Please enter a valid suburb or postcode');
    if (validity.valueMissing) e.currentTarget.setCustomValidity('Enter a suburb or postcode');
    setError(e.currentTarget.validationMessage);
  };

  const handleOnSelect = (item: unknown) => {
    inputRef.current?.setCustomValidity('');
    const { location, state, postcode } = item as AddressLookupResult;
    setValue(`${location} ${state} ${postcode}`);
    selectOption({ state: state, postcode: postcode.toString() });
  };

  const handleManualOption = () => {
    if (!isLoading) {
      setValue('');
      handleManualMode?.();
    }
  };
  const renderOption = (item: unknown) => {
    const { location, state, postcode } = item as AddressLookupResult;
    const listItem = `<span>${location} ${state} ${postcode}</span>`;
    const highlighted = listItem.replaceAll(value.toUpperCase(), `<strong>${value.toUpperCase()}</strong>`);
    return parse(highlighted);
  };

  return (
    <>
      {error && (
        <span
          id='address-lookup-error'
          className={styles.errorLabel}
          role='alert'
          aria-atomic='true'
          data-testid={INLINE_EDD_INPUT_ERROR_TEST_ID}
        >
          {error}
        </span>
      )}
      <AsyncAutosuggest
        value={value}
        required
        onInput={handleOnInput}
        handleChange={(e) => setValue(e.target.value)}
        label='PostcodeLookup'
        name='postcodeLookup'
        minLength={3}
        placeholder='Suburb or postcode'
        variant='small'
        limit={10}
        isError={!!error}
        fetchData={getLocalityData}
        autoComplete='off'
        aria-describedby='address-lookup-error'
        getOptionValue={(item: unknown) => {
          const { location, state, postcode } = (item as Partial<AddressLookupResult>) || {};
          return `${location ?? ''} ${state ?? ''} ${postcode ?? ''}`.trim();
        }}
        renderItem={renderOption}
        renderOption={isLoading ? 'Searching...' : `Can’t find the suburb or postcode? Enter it manually`}
        handleExtraOption={handleManualOption}
        handleSelect={handleOnSelect}
        noContentMessage=''
        cacheItems={false}
        showOptionsOnFocusWithoutValueChange={false}
        data-testid={INLINE_EDD_SEARCH_INPUT_TEST_ID}
        onInvalid={handleOnInvalid}
        ref={inputRef}
      />
    </>
  );
};

export default AddressAutosuggest;
